import React, { useState } from 'react';
import { Tabs, Select, Row, Col, Card, Spin } from 'antd';
import { FileTextOutlined, ExclamationCircleOutlined, BookOutlined, FilterOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { appsApi } from '../api/apps';
import { App } from '../types';
import Versions from './Versions';
import ForceUpdates from './ForceUpdates';
import MinimumVersions from './MinimumVersions';

const { TabPane } = Tabs;
const { Option } = Select;

const VersionManagement: React.FC = () => {
  const [selectedAppId, setSelectedAppId] = useState<number | undefined>(undefined);
  const queryClient = useQueryClient();

  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.getApps(),
  });

  // Function to handle tab change and force refetch
  const handleTabChange = (activeKey: string) => {
    // Force refetch data for the active tab
    switch (activeKey) {
      case 'versions':
        queryClient.invalidateQueries({ queryKey: ['versions'] });
        break;
      case 'force-updates':
        queryClient.invalidateQueries({ queryKey: ['force-updates'] });
        break;
      case 'minimum-versions':
        queryClient.invalidateQueries({ queryKey: ['minimum-version-markdowns'] });
        break;
    }
  };

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <FilterOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
          </Col>
          <Col>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Lọc theo App:</span>
          </Col>
          <Col flex="auto">
            <Select
              placeholder="Chọn app để lọc (để trống để xem tất cả)"
              style={{ width: '100%', maxWidth: 400 }}
              allowClear
              value={selectedAppId}
              onChange={setSelectedAppId}
              loading={appsLoading}
              notFoundContent={appsLoading ? <Spin size="small" /> : 'Không có app nào'}
            >
              {apps?.map((app: App) => (
                <Option key={app.id} value={app.id}>
                  {app.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Tabs 
        defaultActiveKey="versions" 
        type="card"
        size="large"
        onChange={handleTabChange}
        style={{
          '--ant-tabs-card-bg': '#f8f9fa',
          '--ant-tabs-card-active-bg': '#1890ff',
          '--ant-tabs-card-active-color': '#fff',
          '--ant-tabs-card-border-radius': '8px',
        } as React.CSSProperties}
        tabBarStyle={{
          marginBottom: 0,
          borderBottom: 'none',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '8px 8px 0 0',
          padding: '4px 4px 0 4px'
        }}
      >
        <TabPane 
          tab={
            <span style={{ 
              fontWeight: 'bold',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileTextOutlined />
              Versions
            </span>
          } 
          key="versions"
        >
          <div style={{ minHeight: '400px' }}>
            <Versions selectedAppId={selectedAppId} />
          </div>
        </TabPane>
        <TabPane 
          tab={
            <span style={{ 
              fontWeight: 'bold',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ExclamationCircleOutlined />
              Force Updates
            </span>
          } 
          key="force-updates"
        >
          <div style={{ minHeight: '400px' }}>
            <ForceUpdates selectedAppId={selectedAppId} />
          </div>
        </TabPane>
        <TabPane 
          tab={
            <span style={{ 
              fontWeight: 'bold',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <BookOutlined />
              Minimum Versions
            </span>
          } 
          key="minimum-versions"
        >
          <div style={{ minHeight: '400px' }}>
            <MinimumVersions selectedAppId={selectedAppId} />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default VersionManagement;
